import { useEffect, useState } from "react";
import api, { downloadBlob, todayStr, fmtDateTime } from "../lib/api";
import Topbar from "../components/Topbar";
import EmptyState from "../components/EmptyState";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { BookOpen, Upload, FileText, ExternalLink, Download, X, CheckCircle2, Clock } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminSupplierCodeOfConduct() {
  const [history, setHistory] = useState([]);
  const [current, setCurrent] = useState(null);
  const [acceptances, setAcceptances] = useState([]);
  const [tab, setTab] = useState("documento");
  const [showUpload, setShowUpload] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const h = await api.get("/admin/supplier-code-of-conduct");
      setHistory(h.data || []);
      setCurrent((h.data || []).find((d) => d.active) || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al cargar el documento.");
    } finally {
      setLoading(false);
    }
  };

  const loadAcceptances = async () => {
    try {
      const r = await api.get("/admin/supplier-code-of-conduct/acceptances");
      setAcceptances(r.data || []);
    } catch {
      setAcceptances([]);
    }
  };

  useEffect(() => {
    load();
    loadAcceptances();
  }, []);

  const pdfUrl = (doc) => `${BACKEND_URL}${doc.pdf_url}`;

  const exportAcceptances = async () => {
    try {
      const res = await api.get("/admin/supplier-code-of-conduct/acceptances/export", { responseType: "blob" });
      downloadBlob(res.data, `aceptaciones_codigo_conducta_${todayStr()}.xlsx`);
      toast.success("Exportacion generada.");
    } catch {
      toast.error("Error al exportar.");
    }
  };

  return (
    <>
      <Topbar title="Código de Conducta para Proveedores" subtitle="Publica y versiona el código de conducta. Los proveedores deben aceptarlo obligatoriamente." />
      <div className="p-6 space-y-6 pb-28" data-testid="supplier-code-admin-page">

        {/* Tabs */}
        <div className="flex gap-1 bg-slate-100 p-1 rounded-xl w-fit">
          {[
            { key: "documento", label: "Documento" },
            { key: "aceptaciones", label: `Aceptaciones (${acceptances.length})` },
          ].map((t) => (
            <button key={t.key} data-testid={`tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${tab === t.key ? "bg-white shadow-sm text-[#1A1A1A]" : "text-slate-500 hover:text-[#1A1A1A]"}`}>
              {t.label}
            </button>
          ))}
        </div>

        {tab === "documento" && (
          <>
            {/* Versión activa */}
            <section>
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-display font-bold text-base text-[#1A1A1A]">Versión activa</h2>
                <button data-testid="code-upload-open-btn" onClick={() => setShowUpload(true)}
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-sm font-bold">
                  <Upload className="w-4 h-4" /> Subir nueva versión
                </button>
              </div>
              {loading ? (
                <div className="text-sm text-slate-500">Cargando...</div>
              ) : current ? (
                <div data-testid="code-current-card" className="bg-white rounded-2xl border border-slate-200 p-5 flex items-start gap-4">
                  <div className="w-11 h-11 rounded-xl bg-[#FFFBE6] border border-[#FFDF00] flex items-center justify-center flex-shrink-0">
                    <BookOpen className="w-5 h-5 text-[#1A1A1A]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-[#1A1A1A]">{current.title}</div>
                    <div className="text-xs text-slate-500 mt-1">
                      Versión {current.version_number} · vigente desde {current.effective_date}
                    </div>
                    <div className="text-xs text-slate-400 mt-0.5">
                      Aplica a: {(current.applies_to_supplier_types || ["insumos", "servicios"]).join(", ")}
                    </div>
                    {current.notes && (
                      <div className="text-xs text-slate-500 mt-1 italic">{current.notes}</div>
                    )}
                    <a href={pdfUrl(current)} target="_blank" rel="noopener noreferrer"
                      data-testid="code-current-view-pdf"
                      className="inline-flex items-center gap-1.5 mt-3 text-sm font-semibold text-[#1A1A1A] hover:underline">
                      <ExternalLink className="w-4 h-4" /> Ver PDF
                    </a>
                  </div>
                </div>
              ) : (
                <EmptyState title="Aún no publicas ningún código de conducta" message="Sube el primer documento para que tus proveedores lo acepten antes de operar." />
              )}
            </section>

            {/* Histórico */}
            {history.length > 0 && (
              <section>
                <h2 className="font-display font-bold text-base text-[#1A1A1A] mb-3">Histórico de versiones</h2>
                <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-xs text-slate-500 uppercase">
                      <tr>
                        <th className="text-left px-4 py-2.5">Versión</th>
                        <th className="text-left px-4 py-2.5">Titulo</th>
                        <th className="text-left px-4 py-2.5">Vigencia</th>
                        <th className="text-left px-4 py-2.5">Publicado</th>
                        <th className="text-left px-4 py-2.5">Estado</th>
                        <th className="px-4 py-2.5"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.map((d) => (
                        <tr key={d.id} data-testid={`code-history-row-${d.version_number}`} className="border-t border-slate-100">
                          <td className="px-4 py-2.5 font-bold">v{d.version_number}</td>
                          <td className="px-4 py-2.5">{d.title}</td>
                          <td className="px-4 py-2.5 text-slate-500">{d.effective_date}</td>
                          <td className="px-4 py-2.5 text-slate-500">{fmtDateTime(d.uploaded_at)}</td>
                          <td className="px-4 py-2.5">
                            {d.active
                              ? <span className="text-[11px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">Activo</span>
                              : <span className="text-[11px] font-semibold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">Sustituido</span>}
                          </td>
                          <td className="px-4 py-2.5 text-right">
                            <a href={pdfUrl(d)} target="_blank" rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-[#1A1A1A] hover:underline text-xs font-semibold">
                              <FileText className="w-3.5 h-3.5" /> PDF
                            </a>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}
          </>
        )}

        {tab === "aceptaciones" && (
          <section data-testid="code-acceptances-section">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-display font-bold text-base text-[#1A1A1A]">
                Registro de aceptaciones
              </h2>
              <button data-testid="code-export-btn" onClick={exportAcceptances}
                className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 rounded-xl text-sm font-semibold">
                <Download className="w-4 h-4" /> Exportar Excel
              </button>
            </div>
            {acceptances.length === 0 ? (
              <EmptyState title="Sin aceptaciones aún" message="Cuando los proveedores acepten el documento, aparecerán aquí." />
            ) : (
              <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-xs text-slate-500 uppercase">
                    <tr>
                      <th className="text-left px-4 py-2.5">Proveedor</th>
                      <th className="text-left px-4 py-2.5">Email</th>
                      <th className="text-left px-4 py-2.5">Versión</th>
                      <th className="text-left px-4 py-2.5">Fecha</th>
                      <th className="text-left px-4 py-2.5">IP</th>
                    </tr>
                  </thead>
                  <tbody>
                    {acceptances.map((a) => (
                      <tr key={a.id} data-testid={`code-acceptance-row-${a.id}`} className="border-t border-slate-100">
                        <td className="px-4 py-2.5 font-medium">
                          {a.audit_metadata?.supplier_name || "-"}
                        </td>
                        <td className="px-4 py-2.5 text-slate-500">{a.user_email}</td>
                        <td className="px-4 py-2.5">v{a.code_version_number}</td>
                        <td className="px-4 py-2.5 text-slate-500">{fmtDateTime(a.accepted_at)}</td>
                        <td className="px-4 py-2.5 text-slate-400 text-xs font-mono">{a.ip_address || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}
      </div>

      {showUpload && (
        <UploadModal
          onClose={() => setShowUpload(false)}
          onDone={() => { setShowUpload(false); load(); loadAcceptances(); }}
        />
      )}
    </>
  );
}

function UploadModal({ onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("Código de Conducta para Proveedores de Chizy Chiz");
  const [effectiveDate, setEffectiveDate] = useState(todayStr());
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const submit = async () => {
    if (!file) { toast.error("Adjunta un archivo PDF."); return; }
    if (!title.trim()) { toast.error("El título es obligatorio."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("pdf", file);
      fd.append("title", title.trim());
      fd.append("effective_date", effectiveDate);
      if (notes.trim()) fd.append("notes", notes.trim());
      await api.post("/admin/supplier-code-of-conduct", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Nueva versión del código de conducta publicada.");
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al subir el documento.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => !busy && onClose()}>
      <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
        data-testid="code-upload-modal"
        className="bg-white rounded-2xl border border-slate-200 p-6 w-full max-w-lg max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div className="font-display font-bold text-base text-[#1A1A1A]">Subir nueva versión</div>
          <button data-testid="code-upload-close-btn" onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="text-xs font-bold text-slate-600 mb-1.5 block">Archivo PDF *</label>
            <input data-testid="code-upload-file" type="file" accept="application/pdf"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="w-full text-sm file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-[#FFDF00] file:font-semibold file:text-[#1A1A1A]" />
          </div>
          <div>
            <label className="text-xs font-bold text-slate-600 mb-1.5 block">Título *</label>
            <input data-testid="code-upload-title" type="text" value={title} onChange={(e) => setTitle(e.target.value)}
              className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]" />
          </div>
          <div>
            <label className="text-xs font-bold text-slate-600 mb-1.5 block">Fecha de vigencia *</label>
            <input data-testid="code-upload-date" type="date" value={effectiveDate} onChange={(e) => setEffectiveDate(e.target.value)}
              className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]" />
          </div>
          <div>
            <label className="text-xs font-bold text-slate-600 mb-1.5 block">Notas (opcional)</label>
            <textarea data-testid="code-upload-notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
              className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]" />
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-800">
            Al publicar esta versión, todos los proveedores activos que no la hayan aceptado verán un aviso bloqueante al entrar al portal.
          </div>
          {confirming ? (
            <div className="space-y-3">
              <p className="text-sm text-slate-600">¿Confirmas que quieres publicar esta nueva versión? Los proveedores van a necesitar aceptarla.</p>
              <div className="flex gap-2">
                <button onClick={() => setConfirming(false)} disabled={busy}
                  className="px-4 py-2 bg-white border border-slate-200 rounded-lg text-sm font-semibold">Cancelar</button>
                <button data-testid="code-upload-confirm-btn" onClick={submit} disabled={busy}
                  className="btn-primary-chizy flex-1 py-2 rounded-lg text-sm disabled:opacity-50">
                  {busy ? "Publicando..." : "Sí, publicar"}
                </button>
              </div>
            </div>
          ) : (
            <button data-testid="code-upload-submit-btn" onClick={() => setConfirming(true)}
              className="btn-primary-chizy w-full py-2.5 rounded-xl text-sm">
              Publicar nueva versión
            </button>
          )}
        </div>
      </motion.div>
    </div>
  );
}
